import { useMemo } from 'react';

import { METRICS_COLUMNS } from '@/Pages/DatasetSessionCompare/constants';
import { isRealRun } from '@/Pages/DatasetSessionCompare/utils/isRealRun';
import { calculateLatencyNumber } from '@/Pages/Run/components/RunStatsCard';
import { RawCostChip, RawTokensChip } from '@/Pages/Run/components/TokensChip';
import { LatencyChip } from '@/components/RunLatencyChip';
import { ExampleSchemaWithRunsAndOptionalFields } from '@/types/schema';

import { calculateRunStats } from '../../../utils/calculateRunStats';
import { StatusMetricChips } from '../../AggregateFeedbackChips';
import { Loader } from '../Loader';
import { SingleExperimentCell } from '../SingleExperimentCell';

type MetricType = keyof typeof METRICS_COLUMNS;

export const MetricsColumn = ({
  metricType,
  sessionId,
  columnIndex,
  example,
  columnWidthStorage,
  isTruncated,
  experimentInProgress,
  backgroundStyles,
}: {
  metricType: MetricType;
  sessionId: string;
  columnIndex: number;
  example: ExampleSchemaWithRunsAndOptionalFields;
  columnWidthStorage: number[];
  isTruncated: boolean;
  experimentInProgress: boolean;
  backgroundStyles: string;
}) => {
  const runs = useMemo(
    () => example.runs.filter((run) => run.session_id === sessionId),
    [example.runs, sessionId]
  );

  const runStats = useMemo(() => calculateRunStats(runs), [runs]);

  const { numSuccess, numError, numPending, numInterrupted } = useMemo(() => {
    return runs.reduce(
      (acc, run) => {
        switch (run.status) {
          case 'success':
            acc.numSuccess++;
            break;
          case 'error':
            acc.numError++;
            break;
          case 'pending':
            acc.numPending++;
            break;
          case 'interrupted':
            acc.numInterrupted++;
            break;
        }
        return acc;
      },
      {
        numSuccess: 0,
        numError: 0,
        numPending: 0,
        numInterrupted: 0,
      }
    );
  }, [runs]);

  const avgLatency = useMemo(() => {
    const allLatencyValues = runs.reduce((acc, run) => {
      const { start_time, end_time } = run;
      const latency = end_time
        ? calculateLatencyNumber(start_time, end_time)
        : undefined;
      if (latency != null) {
        acc.push(latency);
      }
      return acc;
    }, [] as number[]);
    return (
      allLatencyValues.reduce((acc, val) => acc + val, 0) /
      allLatencyValues.length
    );
  }, [runs]);

  const isLoading =
    experimentInProgress || (runs.length && !runs.every(isRealRun));

  const renderMetricContent = () => {
    if (isLoading) {
      return <Loader />;
    }

    switch (metricType) {
      case 'latency':
        return runs.length > 0 && !isNaN(avgLatency) ? (
          <LatencyChip
            latency={avgLatency}
            className="w-fit px-1.5"
            size="xs"
            simple
          />
        ) : null;

      case 'completed':
        return (
          <StatusMetricChips
            numSuccess={numSuccess}
            numError={numError}
            numPending={numPending}
            numInterrupted={numInterrupted}
            simple
            successName="completed"
          />
        );

      case 'tokens':
        return <RawTokensChip {...runStats} simple />;

      case 'cost':
        return (
          <RawCostChip {...runStats} totalCost={runStats.totalCost} simple />
        );

      default:
        return null;
    }
  };

  return (
    <SingleExperimentCell
      key={`${metricType}-column`}
      columnWidthStorage={columnWidthStorage}
      columnIndex={columnIndex}
      isTruncated={isTruncated}
      minColumnWidth={METRICS_COLUMNS[metricType].minWidth}
      className={backgroundStyles}
    >
      {renderMetricContent()}
    </SingleExperimentCell>
  );
};
